import {
  ShieldAlert,
  CircleDollarSign,
  Home,
  ArrowRight,
  Handshake,
  Wallet,
  Building2,
  Shield,
} from "lucide-react";

// Complete catalogue of everything we do — visible at a glance.
const PRIMARY = [
  {
    id: "pre-foreclosure",
    icon: ShieldAlert,
    eyebrow: "Homeowners in crisis",
    title: "Stop foreclosure — before it starts",
    desc: "Fell behind? We negotiate reinstatement, forbearance, modification, short sale, or fast cash exit.",
    cta: "Pre-foreclosure help",
  },
  {
    id: "surplus-funds",
    icon: CircleDollarSign,
    eyebrow: "Already foreclosed",
    title: "Recover surplus funds you're owed",
    desc: "Home sold at auction for more than the mortgage? That money is legally yours — we recover it.",
    cta: "Surplus funds recovery",
  },
  {
    id: "sell-fast",
    icon: Home,
    eyebrow: "Motivated sellers",
    title: "Sell as-is, in as little as 7 days",
    desc: "Cash offers, no agent commissions, no repairs required. Close on your timeline.",
    cta: "Sell your house fast",
  },
];

const PROGRAMS = [
  {
    id: "agents",
    icon: Handshake,
    label: "Partner Programs",
    tag: "Real Estate Pros",
    desc: "Agents, probate attorneys, property managers, contractors — mutual referral partnerships.",
  },
  {
    id: "cash-buyers",
    icon: Wallet,
    label: "Cash Buyer List",
    tag: "Investors",
    desc: "Get off-market deals matched to your buy box. Free to join. No minimums.",
  },
  {
    id: "wholesalers",
    icon: Building2,
    label: "Wholesaler Desk",
    tag: "Deal Submissions",
    desc: "Submit deals with photos. Our broker sets pricing and moves them through our buyer network.",
  },
  {
    id: "veterans",
    icon: Shield,
    label: "Veteran Housing",
    tag: "Program",
    desc: "Housing provider network connecting veterans with landlords, apartments, and rental resources.",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 md:py-28 bg-[#FAF9F6]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-12 lg:mb-14">
          <div className="mb-4">
            <span className="gold-rule" />
            <span className="text-xs uppercase tracking-[0.24em] text-[#2C5545] font-medium">
              Everything we do — at a glance
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0B1B3D] leading-tight">
            Whatever stage you're in, <em className="text-[#C5A059] not-italic font-medium">we have a plan.</em>
          </h2>
          <p className="mt-5 text-[#64748B] leading-relaxed">
            Three ways we rescue homeowners — plus four industry programs that
            keep our network strong. Tap any tile to jump straight to the details.
          </p>
        </div>

        {/* Primary homeowner services — 3 large cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {PRIMARY.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={`service-card-${s.id}`}
              className="group relative rounded-lg bg-white border border-[#E5E3DB] p-7 transition-all hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(11,27,61,0.08)] hover:border-[#C5A059]/40"
            >
              <div className="h-12 w-12 rounded-lg bg-[#0B1B3D]/5 border border-[#0B1B3D]/10 flex items-center justify-center mb-5 group-hover:bg-[#C5A059]/15 group-hover:border-[#C5A059]/40 transition-colors">
                <s.icon className="h-5 w-5 text-[#0B1B3D]" />
              </div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#2C5545] mb-2">
                {s.eyebrow}
              </div>
              <h3 className="font-display text-xl lg:text-2xl text-[#0B1B3D] leading-snug mb-3">
                {s.title}
              </h3>
              <p className="text-sm text-[#64748B] leading-relaxed mb-5">{s.desc}</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0B1B3D]">
                {s.cta}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </a>
          ))}
        </div>

        {/* Industry programs — 4 compact tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {PROGRAMS.map((p) => (
            <a
              key={p.id}
              href={`#${p.id}`}
              data-testid={`program-card-${p.id}`}
              className="group rounded-lg bg-[#0B1B3D] text-white border border-[#0B1B3D] p-5 transition-all hover:bg-[#1A2F5C] hover:border-[#C5A059]/50"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="h-9 w-9 rounded-md bg-[#C5A059]/15 border border-[#C5A059]/30 flex items-center justify-center">
                  <p.icon className="h-4 w-4 text-[#C5A059]" />
                </div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] font-medium">
                  {p.tag}
                </span>
              </div>
              <div className="font-display text-lg mb-2 leading-tight">{p.label}</div>
              <p className="text-xs text-white/65 leading-relaxed mb-3">{p.desc}</p>
              <span className="inline-flex items-center gap-1 text-xs font-medium text-[#C5A059]">
                Explore
                <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
